/** Боковая карточка оффера (`card=offer:<кабинет>:<offer_key>`, ручка `/offer`): шапка (фото,
 *  артикул, nm, название), регионы оффера «сейчас» (строки витрины как есть; цвет дней FBO+FBS —
 *  по `deficit_class`), график по дням — FBO по регионам стопкой и заказы FBO/FBS отдельным
 *  графиком (своя шкала) — с плашкой правила 8 «оценка пересчитана сборкой» и пометкой неполных
 *  дней (правило 7), в подсказке дня — разбивка FBO «надёжно / слабо»; поставки оффера
 *  (`/offer.supplies`, новые сверху) со ссылкой в карточку поставки. Плашки свежести своего
 *  ответа (сборка не прошла, другая сборка, сборка ≠ сборке страницы) — `BuildBanners`. */
import { PanelRight } from "lucide-react";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  WbAggregate,
  WbOffer,
  WbOfferCard,
  WbOfferCell,
  WbOfferSupply,
  WbRegionsState,
} from "@/api/wbRegions";
import { mpProductUrl } from "@/lib/marketplace";
import { useIsDark } from "@/lib/use-is-dark";
import { cn } from "@/lib/utils";
import { compact, fmtWhen, intSigned } from "../ozon-analytics/format";
import { tdNum, thClass } from "../ozon-analytics/ui";
import { BuildBanners } from "./BuildBanners";
import { useBuildSync } from "./buildSync";
import {
  Confidence,
  Days,
  DeficitCountsView,
  ErrorBox,
  IncompleteMark,
  Loading,
  Missing,
  OfferPhoto,
  PerDay,
  Qty,
} from "./cells";
import { confidenceTitle, datesText, deficitClassName, deficitLabel, qtyText } from "./format";
import { historyPoints, incompleteText, type HistoryPoint } from "./history";
import { regionLabel, sum2 } from "./matrix";
import { useWbOffer } from "./queries";
import { SidePanel } from "./SidePanel";
import { ABROAD, UNKNOWN, type CardRef, type OfferCardRef } from "./state";
import { methodLabel, PLANNED_LABEL, storageLabel } from "./supplyFormat";

/** Цвет ряда — по региону (сущности), а не по месту в стопке: категориальная палитра, слоты по
 *  порядку макрорегионов контракта, «Зарубеж» — 7-й, «Не опр.» — нейтральный серый. */
const MACRO_ORDER = [
  "Центральный",
  "Северо-Западный",
  "Южный и Северо-Кавказский",
  "Приволжский",
  "Уральский",
  "Дальневосточный и Сибирский",
];
const SLOTS_LIGHT = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6"];
const SLOTS_DARK = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9"];
const GRAY = { light: "#8a8986", dark: "#9a9993" };

function seriesColor(key: string, dark: boolean): string {
  const slots = dark ? SLOTS_DARK : SLOTS_LIGHT;
  if (key === ABROAD) return slots[6]!;
  const i = MACRO_ORDER.indexOf(key);
  return i >= 0 ? slots[i]! : dark ? GRAY.dark : GRAY.light;
}

function seriesLabel(key: string): string {
  if (key === ABROAD) return "Зарубеж";
  if (key === UNKNOWN) return "Регион не определён";
  return key;
}

function NowRow({ c }: { c: WbOfferCell }): React.ReactElement {
  return (
    <tr className="border-t" data-testid="card-region-row">
      <td className="px-2 py-1 text-left">{regionLabel(c)}</td>
      <td className={tdNum}>
        <PerDay v={c.orders_28d} />
        <IncompleteMark orders={c.orders_complete} sales={c.sales_complete} />
        <div className="text-[11px] leading-3 text-muted-foreground">
          FBO <PerDay v={c.orders_fbo_28d} /> · FBS <PerDay v={c.orders_fbs_28d} />
        </div>
      </td>
      <td className={tdNum}>
        <Qty v={sum2(c.fbo_stock_known_chain, c.fbo_stock_by_supply_signal)} />
      </td>
      <td className={tdNum}>
        <Qty v={sum2(c.fbo_stock_weak, c.fbo_stock_unresolved)} />
      </td>
      <td className={tdNum}>
        <Confidence v={c.confidence} title={confidenceTitle(c)} />
      </td>
      <td className={tdNum}>
        <Days v={c.cover_days_fbo} demand={c.orders_28d} />
      </td>
      <td className={tdNum}>
        <Qty v={c.fbs_stock} />
      </td>
      <td
        className={cn(tdNum, deficitClassName(c.deficit_class))}
        data-testid="card-cell"
        data-class={c.deficit_class ?? "no_demand"}
        title={deficitLabel(c.deficit_class)}
      >
        <Days v={c.cover_days_total} demand={c.orders_28d} />
        <div className="text-[10px] leading-3">{deficitLabel(c.deficit_class)}</div>
      </td>
      <td className={cn(tdNum, "border-l bg-muted/30")} data-testid="card-frozen">
        <Qty v={c.frozen_stock} /> · <Qty v={c.vanished_stock} />
      </td>
    </tr>
  );
}

function TotalRow({ a }: { a: WbAggregate }): React.ReactElement {
  return (
    <tr className="border-t-2 font-medium">
      <td className="px-2 py-1 text-left">Итого</td>
      <td className={tdNum}>
        <PerDay v={a.orders_28d} />
      </td>
      <td className={tdNum}>
        <Qty v={sum2(a.fbo_stock_known_chain, a.fbo_stock_by_supply_signal)} />
      </td>
      <td className={tdNum}>
        <Qty v={sum2(a.fbo_stock_weak, a.fbo_stock_unresolved)} />
      </td>
      <td className={tdNum}>
        <Confidence v={a.confidence} title={confidenceTitle(a)} />
      </td>
      <td className={tdNum}>
        <Days v={a.cover_days_fbo} demand={a.orders_28d} />
      </td>
      <td className={tdNum}>
        <Qty v={a.fbs_stock} />
      </td>
      <td className="px-2 py-1 text-right">
        <Days v={a.cover_days_total} demand={a.orders_28d} />
        <div>
          <DeficitCountsView counts={a.deficit_counts} />
        </div>
      </td>
      <td className={cn(tdNum, "border-l bg-muted/30")}>
        <Qty v={a.frozen_stock} /> · <Qty v={a.vanished_stock} />
      </td>
    </tr>
  );
}

function OfferBlock({ o }: { o: WbOffer }): React.ReactElement {
  const url = mpProductUrl("wb", o.mp_sku);
  return (
    <section className="space-y-3" data-testid="card-offer">
      <div className="flex items-start gap-3">
        <OfferPhoto url={o.image_url} alt={o.name ?? o.vendor_code ?? "оффер"} size="large" />
        <div className="min-w-0 space-y-1 text-sm">
          <h2 className="text-base font-semibold">
            {o.vendor_code ?? "без артикула"}{" "}
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="text-sm font-normal text-muted-foreground hover:underline">
                nm {o.mp_sku}
              </a>
            )}
          </h2>
          <div>{o.name ?? <span className="italic text-muted-foreground">нет названия WB</span>}</div>
          <div className="text-muted-foreground">
            1С: {o.name_1c ?? "—"}
            {o.article_1c && <> · {o.article_1c}</>} · сопоставление: {o.mapping_status ?? "нет статуса"}
          </div>
          <div className="text-xs text-muted-foreground">Кабинет: {o.legal_name}</div>
          {o.dim_missing && (
            <div className="text-xs text-muted-foreground" data-testid="dim-missing">
              {o.dim_label ?? "нет в mp_finance_sku_dim"}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              <th className={cn(thClass, "text-left")}>Регион</th>
              <th className={thClass}>Спрос/день</th>
              <th className={thClass}>FBO надёжно</th>
              <th className={thClass}>слабо / не опр.</th>
              <th className={thClass}>Уверенность</th>
              <th className={thClass}>Дни FBO</th>
              <th className={thClass} title="Регион нашего склада, отгружает в любой регион">FBS*</th>
              <th className={thClass}>Дни FBO+FBS</th>
              <th className={cn(thClass, "border-l")} title="Справочно: заморожено · исчезнувший склад">
                Заморожено
              </th>
            </tr>
          </thead>
          <tbody>
            {o.regions.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-2 py-2 text-muted-foreground">
                  «Сейчас» строк нет — оффер есть только в истории.
                </td>
              </tr>
            ) : (
              o.regions.map((c) => <NowRow key={`${c.region_kind}:${c.region_wb ?? ""}`} c={c} />)
            )}
            {o.regions.length > 0 && <TotalRow a={o.total} />}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function HistoryTip({
  active,
  payload,
  keys,
}: {
  active?: boolean;
  payload?: { payload: HistoryPoint }[];
  keys: string[];
}): React.ReactElement | null {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  const inc = incompleteText(p);
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs text-foreground shadow-md">
      <div className="mb-1 font-medium">{fmtWhen(p.date)}</div>
      {inc && <div className="mb-1 text-amber-700 dark:text-amber-400">день неполный: {inc}</div>}
      {keys.map((k) => {
        const weak = p.fbo_weak_by[k] ?? null;
        return (
          <div key={k} className="flex justify-between gap-4 tabular-nums" data-testid="tip-region">
            <span>FBO {seriesLabel(k)}</span>
            <span>
              {qtyText(p.fbo[k] ?? null)}
              {weak !== null && weak > 0 && <span className="text-muted-foreground"> (слабо {qtyText(weak)})</span>}
            </span>
          </div>
        );
      })}
      <div className="flex justify-between gap-4 tabular-nums" data-testid="tip-fbo-split">
        <span>FBO надёжно · слабо / не опр.</span>
        <span>
          {qtyText(p.fbo_reliable)} · {qtyText(p.fbo_weak)}
        </span>
      </div>
      <div className="mt-1 flex justify-between gap-4 border-t pt-1 tabular-nums">
        <span>Заказы FBO · FBS</span>
        <span>
          {qtyText(p.orders_fbo)} · {qtyText(p.orders_fbs)}
        </span>
      </div>
      <div className="flex justify-between gap-4 tabular-nums">
        <span>Продажи</span>
        <span>{qtyText(p.sales_qty)}</span>
      </div>
    </div>
  );
}

export function HistoryBlock({ data }: { data: WbOfferCard }): React.ReactElement {
  const isDark = useIsDark();
  const { points, keys } = useMemo(() => historyPoints(data.history), [data.history]);
  const flat = useMemo(
    () => points.map((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [`fbo:${k}`, p.fbo[k] ?? null])) })),
    [points, keys],
  );
  const builtAt = data.state.table_built_at.wb_region_supply_demand;
  const incomplete = points.filter((p) => p.orders_incomplete || p.sales_incomplete);
  const ink = isDark ? "#e7e5e4" : "#1c1917";
  // подписи осей: в светлой — умолчание recharts (#666), в тёмной оно сливается с фоном — тон muted-foreground
  const tick = isDark ? { fontSize: 10, fill: "#94a3b8" } : { fontSize: 10 };
  return (
    <section className="space-y-2" data-testid="card-history">
      <h3 className="text-sm font-medium">По дням</h3>
      <div
        role="note"
        data-testid="history-note"
        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
      >
        {builtAt
          ? `Оценка склада пересчитана по данным сборки ${fmtWhen(builtAt)}; не срез на дату.`
          : data.history_recomputed_note}
      </div>
      {points.length === 0 ? (
        <div className="text-sm text-muted-foreground">Истории по дням нет.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {keys.map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="inline-block size-2.5 rounded-sm" style={{ background: seriesColor(k, isDark) }} />
                {seriesLabel(k)}
              </span>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">FBO-остаток по регионам, шт. (оценка)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={flat} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} strokeOpacity={0.25} />
              <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={false} />
              <YAxis tick={tick} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => compact(v)} />
              <Tooltip cursor={{ fillOpacity: 0.06 }} content={<HistoryTip keys={keys} />} />
              {keys.map((k) => (
                <Bar
                  key={k}
                  dataKey={`fbo:${k}`}
                  name={seriesLabel(k)}
                  stackId="fbo"
                  fill={seriesColor(k, isDark)}
                  stroke={isDark ? "#0b0b0b" : "#ffffff"}
                  strokeWidth={1}
                  maxBarSize={24}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center gap-x-4 text-xs text-muted-foreground">
            <span>Заказы за день, шт. (своя шкала)</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-4" style={{ background: ink }} /> FBO
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0 w-4 border-t-2 border-dashed" style={{ borderColor: ink }} /> FBS
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={flat} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} strokeOpacity={0.2} />
              <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={false} />
              <YAxis tick={tick} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => compact(v)} />
              <Tooltip content={<HistoryTip keys={keys} />} />
              <Line dataKey="orders_fbo" name="Заказы FBO" stroke={ink} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line dataKey="orders_fbs" name="Заказы FBS" stroke={ink} strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
          {incomplete.length > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="history-incomplete">
              * день неполный:{" "}
              {incomplete.map((p) => `${fmtWhen(p.date)} (${incompleteText(p)})`).join("; ")} — заказы и
              продажи за него не окончательные.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function OfferSupplies({
  supplies,
  onOpenCard,
}: {
  supplies: WbOfferSupply[];
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  return (
    <section className="space-y-1" data-testid="offer-supplies">
      <h3 className="text-sm font-medium">Поставки оффера</h3>
      {supplies.length === 0 ? (
        <div className="text-xs text-muted-foreground">Поставок оффера в витрине нет.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>№</th>
                <th className={cn(thClass, "text-left")}>Дата · статус</th>
                <th className={cn(thClass, "text-left")}>Оценка региона · метод</th>
                <th className={thClass} title="товара оффера в поставке">Принято</th>
                <th className={thClass}>В хранении</th>
                <th className={thClass}>Продано</th>
                <th className={thClass}>Расхожд.</th>
              </tr>
            </thead>
            <tbody>
              {supplies.map((s) => (
                <tr key={`${s.legal_name}:${s.supply_id}:${s.mp_size}`} className="border-t align-top" data-testid="offer-supply">
                  <td className="px-2 py-1">
                    {s.in_tracking ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-medium tabular-nums hover:underline"
                        title="Открыть карточку поставки"
                        onClick={() => onOpenCard({ kind: "supply", legal: s.legal_name, supplyId: s.supply_id })}
                      >
                        {s.supply_id}
                        <PanelRight className="size-3 text-muted-foreground" aria-hidden />
                      </button>
                    ) : (
                      <span className="tabular-nums">{s.supply_id}</span>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    {s.in_tracking ? (
                      <>
                        {s.supply_date ? fmtWhen(s.supply_date) : <Missing text="без даты" />}
                        <div className="text-muted-foreground">
                          {s.status_name ?? "нет статуса WB"} · {storageLabel(s.storage_status)}
                          {s.planned && (
                            <span className="ml-1 rounded bg-sky-100 px-1 text-sky-900 dark:bg-sky-950 dark:text-sky-300" data-testid="planned">
                              {PLANNED_LABEL}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400" data-testid="not-in-tracking">
                        поставки нет в wb_supply_tracking — карточки нет
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    {s.in_tracking && (
                      <>
                        {s.region_wb_est ?? <Missing text="регион не определён" />}
                        <span className="text-muted-foreground"> · {methodLabel(s.method).short}</span>
                        {s.attr19_mismatch && (
                          <div className="text-red-700 dark:text-red-400" data-testid="offer-supply-attr19">
                            attr19 ≠ данным
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className={tdNum}>
                    <Qty v={s.item.qty_accepted} />
                  </td>
                  <td className={tdNum}>
                    <Qty v={s.item.in_storage_now} />
                  </td>
                  <td className={tdNum}>
                    <Qty v={s.item.sold_fbo} />
                  </td>
                  <td className={tdNum}>{s.item.recon_diff === null ? <Missing /> : intSigned(s.item.recon_diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OfferCardBody({
  card,
  pageState,
  onOpenCard,
}: {
  card: OfferCardRef;
  pageState: WbRegionsState;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const q = useWbOffer(card.legal, card.offerKey);
  const mixed = useBuildSync([pageState, q.data?.state]);
  if (q.isLoading) return <Loading text="Загружаю карточку оффера…" />;
  if (q.isError) return <ErrorBox error={q.error} what="карточку оффера (/offer)" />;
  const data = q.data!;
  return (
    <div className="space-y-4">
      <BuildBanners states={[data.state]} mixed={mixed} />
      {data.offers.map((o) => (
        <OfferBlock key={o.legal_name} o={o} />
      ))}
      <div className="text-[11px] text-muted-foreground">
        «Сейчас»: спрос, FBO и дни — на {datesText(data.dates, "fbo_stock_date")}; FBS* — регион нашего
        склада, отгружает в любой регион, снимок на {datesText(data.dates, "fbs_snapshot_date")};
        заморожено — справочно, на {datesText(data.dates, "frozen_stock_date")}.
      </div>
      <HistoryBlock data={data} />
      <OfferSupplies supplies={data.supplies} onOpenCard={onOpenCard} />
    </div>
  );
}

export function OfferCard({
  card,
  pageState,
  onClose,
  onOpenCard,
}: {
  card: OfferCardRef;
  /** свежесть ответа страницы (/meta) — сборка карточки сверяется с ней */
  pageState: WbRegionsState;
  onClose: () => void;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  return (
    <SidePanel title="Карточка оффера WB" testId="offer-card" resetKey={`${card.legal}:${card.offerKey}`} onClose={onClose}>
      <OfferCardBody card={card} pageState={pageState} onOpenCard={onOpenCard} />
    </SidePanel>
  );
}
